import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { LibraryModule } from '../shared/lib/lib.module';
import { NiTableComponent } from '../shared/lib/ni-table/ni-table.component';
import { NiTableCellDirective } from '../shared/lib/ni-table/ni-table-cell.directive';
import { NiTableColumn } from '../shared/lib/ni-table/ni-table.models';
import { SeniorCenterApplicationService, SeniorCenterApplicationListItem } from '../core/services/senior-center-application.service';
import { FinancialYearFilterService } from '../core/services/financial-year-filter.service';
import { GoogleMapsLoaderService } from '../core/services/google-maps-loader.service';
import { normalizeApplicationStatus } from '../shared/utils/salesforce-application-status';
import { FormDateUtils } from '../shared/utils/form-date-utils';
import { getSelectedLocation } from '../shared/utils/storage-utils';
import {
  exportSeniorCenterQrPdf,
  exportSeniorCenterRfidExcel,
  toExportRow,
} from './senior-center-export.util';
import { StaffRejectModalComponent } from '../program/components/staff-reject-modal.component';
import { PermissionService } from '../core/services/permission.service';
import { StaffPermission } from '../core/constants/staff-roles';
import { BehaviorSubject, Subscription, combineLatest, firstValueFrom, of } from 'rxjs';
import { switchMap, catchError, map, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';

/** AAA Transportation max distance (miles). */
const AAA_TRANSPORTATION_MAX_MILES = 3.5;
/** Metro Transportation max distance (miles). */
const METRO_TRANSPORTATION_MAX_MILES = 7.5;

const FUNDING_AAA = 'AAA Transportation';
const FUNDING_METRO = 'Metro Transportation';

interface StatusFilterConfig {
  key: string;
  label: string;
}

type DownloadType = 'rfid' | 'qrcode';
type SeniorCenterListTab = 'applications' | 'transportation' | 'expiring';
type ExpiryStatusFilter = 'due' | 'expiring' | 'expired';

@Component({
  selector: 'senior-center-applications-component, SeniorCenterApplicationsComponent',
  standalone: true,
  templateUrl: './senior-center-applications.component.html',
  styleUrls: ['./senior-center-applications.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    LibraryModule,
    RouterModule,
    NiTableComponent,
    NiTableCellDirective,
    StaffRejectModalComponent,
  ],
})
export class SeniorCenterApplicationsComponent implements OnInit, OnDestroy {
  @ViewChild('statusFilterContainer') statusFilterContainer?: ElementRef<HTMLElement>;
  @ViewChild('downloadMenuContainer') downloadMenuContainer?: ElementRef<HTMLElement>;

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly seniorCenterService = inject(SeniorCenterApplicationService);
  private readonly financialYearFilterService = inject(FinancialYearFilterService);
  private readonly permissionService = inject(PermissionService);
  private readonly googleMapsLoader = inject(GoogleMapsLoaderService);
  private readonly reloadTrigger$ = new BehaviorSubject<void>(undefined);
  private readonly searchText$ = new BehaviorSubject<string>('');
  private loadSubscription?: Subscription;

  isLoading = true;
  isExporting = false;
  isDownloadModalOpen = false;
  modalExportError: string | null = null;
  errorMessage: string | null = null;
  applications: SeniorCenterApplicationListItem[] = [];
  columns: NiTableColumn<SeniorCenterApplicationListItem>[] = [];
  activeTab: SeniorCenterListTab = 'applications';
  /** Location-scoped counts from the list API (annual reapply window). */
  expiringCount = 0;
  expiredCount = 0;

  selectedStatus = 'all';
  /** Bound to the search input; API search uses debounced searchText$. */
  searchText = '';
  readonly searchPlaceholder = 'Search name, phone, email, DOB, SSN';
  isStatusMenuOpen = false;
  isDownloadMenuOpen = false;
  modalDateFrom: Date | null = null;
  modalDateTo: Date | null = null;
  selectedExportType: DownloadType | null = null;

  reviewingApplication: SeniorCenterApplicationListItem | null = null;
  showTransportApproveModal = false;
  showRejectModal = false;
  isReviewing = false;
  reviewError: string | null = null;
  rejectComment = '';
  /** Editable trip direction in the approve modal (Transportation Approver). */
  reviewTransportationOption = '';
  /** Funding types multi-select: AAA Transportation / Metro Transportation. */
  reviewFundingTypes: string[] = [];
  /** Allow selecting funding types outside mile rules when applicant does not qualify. */
  reviewFundingOverride = false;
  /** Driving distance home → center (miles); null while loading / unavailable. */
  reviewDistanceMiles: number | null = null;
  isLoadingDistance = false;
  distanceLoadError: string | null = null;
  /** True when approving from a previously rejected request (Revoke flow). */
  isRevokingTransportation = false;

  readonly transportationOptionChoices: { label: string; value: string }[] = [
    { label: 'To Center', value: 'To Center' },
    { label: 'To Home', value: 'To Home' },
    { label: 'Round Trip', value: 'Round Trip' },
    { label: 'Not Required', value: 'Not Required' },
  ];

  /** Revoke grants a real trip — do not offer Not Required. */
  readonly revokeTransportationOptionChoices: { label: string; value: string }[] = [
    { label: 'To Center', value: 'To Center' },
    { label: 'To Home', value: 'To Home' },
    { label: 'Round Trip', value: 'Round Trip' },
  ];

  readonly fundingTypeChoices: { label: string; value: string }[] = [
    { label: FUNDING_AAA, value: FUNDING_AAA },
    { label: FUNDING_METRO, value: FUNDING_METRO },
  ];

  get transportModalOptionChoices(): { label: string; value: string }[] {
    return this.isRevokingTransportation
      ? this.revokeTransportationOptionChoices
      : this.transportationOptionChoices;
  }

  get transportModalTitle(): string {
    return this.isRevokingTransportation
      ? 'Revoke Rejection & Approve Transportation?'
      : 'Approve Transportation Request?';
  }

  get transportModalSubtitle(): string {
    return this.isRevokingTransportation
      ? 'Choose trip direction and funding type(s). This updates the transportation request only — the Senior Center application status will not change.'
      : 'Review distance, choose funding type(s), and adjust trip direction if needed. This updates the transportation request only — the Senior Center application status will not change.';
  }

  get isBeyondAaaMiles(): boolean {
    return this.reviewDistanceMiles != null && this.reviewDistanceMiles > AAA_TRANSPORTATION_MAX_MILES;
  }

  get isBeyondMetroMiles(): boolean {
    return this.reviewDistanceMiles != null && this.reviewDistanceMiles > METRO_TRANSPORTATION_MAX_MILES;
  }

  get distanceDisplayLabel(): string {
    if (this.isLoadingDistance) {
      return 'Calculating distance…';
    }
    if (this.reviewDistanceMiles != null && Number.isFinite(this.reviewDistanceMiles)) {
      return `${this.reviewDistanceMiles.toFixed(1)} miles to center`;
    }
    return this.distanceLoadError || 'Distance unavailable';
  }

  get requiresFundingTypes(): boolean {
    const option = (this.reviewTransportationOption || '').trim().toLowerCase();
    return option !== '' && option !== 'not required' && option !== 'no need' && option !== 'none';
  }

  get transportModalConfirmLabel(): string {
    return this.isReviewing ? 'Approving...' : 'Approve Transportation';
  }

  get canAccessTransportationTab(): boolean {
    return this.permissionService.hasPermission(StaffPermission.ReviewTransportation);
  }

  get canAccessApplicationsTab(): boolean {
    return this.permissionService.hasPermission(StaffPermission.ReviewApplications);
  }

  readonly applicationStatusConfigs: StatusFilterConfig[] = [
    { key: 'all', label: 'All Status' },
    { key: 'draft', label: 'Draft' },
    { key: 'pending', label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'returned', label: 'Returned' },
    { key: 'rejected', label: 'Rejected' },
  ];

  /** Transportation tab filters by transportation_request_status, not application status. */
  readonly transportationStatusConfigs: StatusFilterConfig[] = [
    { key: 'all', label: 'All Status' },
    { key: 'pending', label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' },
  ];

  /** Expiring tab filters by annual expiry window (approved + 1 year). */
  readonly expiryStatusConfigs: StatusFilterConfig[] = [
    { key: 'due', label: 'All Due' },
    { key: 'expiring', label: 'Expiring Soon' },
    { key: 'expired', label: 'Expired' },
  ];

  readonly downloadOptions: { key: DownloadType; label: string }[] = [
    { key: 'rfid', label: 'RFID Tags' },
    { key: 'qrcode', label: 'QR Code' },
  ];

  get statusConfigs(): StatusFilterConfig[] {
    if (this.activeTab === 'transportation') {
      return this.transportationStatusConfigs;
    }
    if (this.activeTab === 'expiring') {
      return this.expiryStatusConfigs;
    }
    return this.applicationStatusConfigs;
  }

  get dueRenewalCount(): number {
    return this.expiringCount + this.expiredCount;
  }

  get expiryBannerMessage(): string | null {
    if (this.dueRenewalCount <= 0) {
      return null;
    }
    const parts: string[] = [];
    if (this.expiredCount > 0) {
      parts.push(`${this.expiredCount} expired`);
    }
    if (this.expiringCount > 0) {
      parts.push(`${this.expiringCount} expiring within 60 days`);
    }
    return `Annual reapply: ${parts.join(' · ')}. Open the Expiring tab to review.`;
  }

  ngOnInit(): void {
    const initialTab = this.route.snapshot.queryParamMap.get('tab');
    const initialStatus = this.route.snapshot.queryParamMap.get('status');
    const requestedTab =
      initialTab === 'transportation'
        ? 'transportation'
        : initialTab === 'expiring'
          ? 'expiring'
          : 'applications';
    if (requestedTab === 'transportation' && this.canAccessTransportationTab) {
      this.activeTab = 'transportation';
    } else if (requestedTab === 'expiring' && this.canAccessApplicationsTab) {
      this.activeTab = 'expiring';
    } else if (this.canAccessApplicationsTab) {
      this.activeTab = 'applications';
    } else if (this.canAccessTransportationTab) {
      this.activeTab = 'transportation';
    } else {
      this.activeTab = 'applications';
    }

    if (this.activeTab === 'applications' && initialStatus === 'draft') {
      this.selectedStatus = 'draft';
    } else if (this.activeTab === 'transportation') {
      this.selectedStatus = this.resolveTransportationStatus(initialStatus);
    } else if (this.activeTab === 'expiring') {
      this.selectedStatus = this.resolveExpiryStatus(initialStatus);
    }

    this.columns = this.buildColumns(this.activeTab);

    this.loadSubscription = combineLatest([
      this.financialYearFilterService.selectedYear$,
      this.reloadTrigger$,
      this.searchText$.pipe(debounceTime(350), distinctUntilChanged()),
    ])
      .pipe(
        switchMap(([year, , search]) => {
          this.isLoading = true;
          this.errorMessage = null;
          return this.seniorCenterService
            .getApplications(this.buildListFilters(search))
            .pipe(
              map((res) => ({ res, year })),
              catchError((err) => {
                this.errorMessage = err?.error?.message ?? 'Unable to load Senior Center applications.';
                this.isLoading = false;
                return of(null);
              })
            );
        })
      )
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.expiringCount = Number(result.res.expiringCount ?? 0) || 0;
        this.expiredCount = Number(result.res.expiredCount ?? 0) || 0;

        const skipYearFilter = this.activeTab === 'expiring';
        this.applications = (result.res.applications ?? [])
          .map((app) => this.normalizeApplication(app))
          .filter((app) =>
            skipYearFilter ? true : this.matchesSelectedFinancialYear(app.submittedDate, result.year)
          );
        this.isLoading = false;
      });
  }

  onSearchInput(value: string): void {
    this.searchText = value ?? '';
    this.searchText$.next(this.searchText.trim());
  }

  clearSearch(): void {
    this.searchText = '';
    this.searchText$.next('');
  }

  get selectedStatusLabel(): string {
    return this.statusConfigs.find((status) => status.key === this.selectedStatus)?.label ?? 'All Status';
  }

  get displayedApplications(): SeniorCenterApplicationListItem[] {
    if (this.activeTab !== 'transportation') {
      return this.applications;
    }

    // Transportation tab: any submitted app that requested transport (pending or approved application).
    // Approve/Reject transport still requires application approval (canReviewTransportation).
    return this.applications.filter((app) => {
      if (!this.isEligibleForTransportationTab(app)) {
        return false;
      }
      if (!this.isTransportationRequested(app)) {
        return false;
      }
      return this.matchesTransportationStatusFilter(app);
    });
  }

  get canConfirmExport(): boolean {
    return this.modalDateFrom != null && this.modalDateTo != null && !this.isExporting;
  }

  get downloadModalTitle(): string {
    if (this.selectedExportType === 'rfid') {
      return 'Download RFID Tags';
    }

    if (this.selectedExportType === 'qrcode') {
      return 'Download QR Code';
    }

    return 'Download Applications';
  }

  get tableEmptyMessage(): string {
    const query = this.searchText.trim();
    if (this.activeTab === 'transportation') {
      if (query) {
        return `No transportation requests match "${query}".`;
      }
      if (this.selectedStatus === 'all') {
        return 'No applications with transportation requests found.';
      }
      if (this.selectedStatus === 'pending') {
        return 'No applications pending transportation review.';
      }
      if (this.selectedStatus === 'approved') {
        return 'No transportation requests with Approved status.';
      }
      if (this.selectedStatus === 'rejected') {
        return 'No transportation requests with Rejected status.';
      }
      return 'No transportation requests match the selected status.';
    }

    if (this.activeTab === 'expiring') {
      if (query) {
        return `No expiring or expired applications match "${query}".`;
      }
      if (this.selectedStatus === 'expired') {
        return 'No expired applications found.';
      }
      if (this.selectedStatus === 'expiring') {
        return 'No applications expiring within 60 days.';
      }
      return 'No applications are due to expire or already expired.';
    }

    if (query) {
      return `No applications match "${query}". Try name, phone, email, DOB, or SSN last-4.`;
    }

    return this.selectedStatus === 'all'
      ? 'No Senior Center applications found.'
      : 'No applications match the selected status.';
  }

  /** Leave Senior Center applications and return to the Senior Center tools page. */
  onBackClick(): void {
    void this.router.navigate(['/programs/senior-center']);
  }

  setActiveTab(tab: SeniorCenterListTab): void {
    if (this.activeTab === tab) {
      return;
    }

    if (tab === 'transportation' && !this.canAccessTransportationTab) {
      return;
    }

    if ((tab === 'applications' || tab === 'expiring') && !this.canAccessApplicationsTab) {
      return;
    }

    this.activeTab = tab;
    this.selectedStatus =
      tab === 'transportation' ? 'pending' : tab === 'expiring' ? 'due' : 'all';
    this.isStatusMenuOpen = false;
    this.columns = this.buildColumns(tab);
    this.reloadTrigger$.next();
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        tab: tab === 'applications' ? null : tab,
        status: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  toggleStatusMenu(event: Event): void {
    event.stopPropagation();
    this.isStatusMenuOpen = !this.isStatusMenuOpen;
    if (this.isStatusMenuOpen) {
      this.isDownloadMenuOpen = false;
    }
  }

  toggleDownloadMenu(event: Event): void {
    event.stopPropagation();
    if (this.isExporting) {
      return;
    }

    this.isDownloadMenuOpen = !this.isDownloadMenuOpen;
    if (this.isDownloadMenuOpen) {
      this.isStatusMenuOpen = false;
    }
  }

  onStatusChange(status: string): void {
    this.selectedStatus = status;
    this.isStatusMenuOpen = false;
    // Transportation filters transportation_request_status client-side; Applications/Expiring need a refetch.
    if (this.activeTab !== 'transportation') {
      this.reloadTrigger$.next();
    }
  }

  onDownloadOptionSelected(type: DownloadType): void {
    this.isDownloadMenuOpen = false;
    this.selectedExportType = type;
    this.modalExportError = null;
    this.modalDateFrom = null;
    this.modalDateTo = null;
    this.isDownloadModalOpen = true;
  }

  closeDownloadModal(): void {
    if (this.isExporting) {
      return;
    }

    this.isDownloadModalOpen = false;
    this.modalExportError = null;
  }

  onModalDateFromChange(date: Date | null): void {
    this.modalDateFrom = date;
    this.modalExportError = null;
  }

  onModalDateToChange(date: Date | null): void {
    this.modalDateTo = date;
    this.modalExportError = null;
  }

  async onExportConfirm(): Promise<void> {
    if (!this.canConfirmExport || !this.modalDateFrom || !this.modalDateTo || !this.selectedExportType) {
      return;
    }

    this.isExporting = true;
    this.modalExportError = null;

    try {
      const response = await firstValueFrom(
        this.seniorCenterService.getApplications({
          status: 'approved',
          approvalDateFrom: this.formatLocalDate(this.modalDateFrom),
          approvalDateTo: this.formatLocalDate(this.modalDateTo),
          hasRfid: true,
        })
      );

      const exportRows = (response.applications ?? [])
        .map((app) => this.normalizeApplication(app))
        .map((app) => toExportRow(app, this.getEffectiveApprovedDateString(app)))
        .filter((row): row is NonNullable<typeof row> => row != null);

      if (exportRows.length === 0) {
        this.modalExportError =
          'No approved applications with RFID found for the selected approval date range.';
        return;
      }

      const rangeStamp = this.buildRangeStamp(this.modalDateFrom, this.modalDateTo);
      if (this.selectedExportType === 'rfid') {
        exportSeniorCenterRfidExcel(exportRows, `senior-center-rfid-tags-${rangeStamp}.xlsx`);
      } else {
        await exportSeniorCenterQrPdf(exportRows, `senior-center-qr-cards-${rangeStamp}.pdf`);
      }

      this.isDownloadModalOpen = false;
    } catch {
      this.modalExportError = 'Unable to export applications. Please try again.';
    } finally {
      this.isExporting = false;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;

    if (this.isStatusMenuOpen && !this.statusFilterContainer?.nativeElement.contains(target)) {
      this.isStatusMenuOpen = false;
    }

    if (this.isDownloadMenuOpen && !this.downloadMenuContainer?.nativeElement.contains(target)) {
      this.isDownloadMenuOpen = false;
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
  }

  private buildListFilters(searchOverride?: string) {
    const searchText = (searchOverride ?? this.searchText).trim() || undefined;
    // Transportation tab: load pending + approved apps; client keeps rows that requested transport.
    if (this.activeTab === 'transportation') {
      return {
        searchText,
      };
    }

    if (this.activeTab === 'expiring') {
      const expiryFilter = this.resolveExpiryStatus(this.selectedStatus);
      return {
        expiryFilter,
        searchText,
      };
    }

    return {
      status: this.selectedStatus !== 'all' ? this.selectedStatus : undefined,
      searchText,
    };
  }

  private resolveExpiryStatus(status: string | null | undefined): ExpiryStatusFilter {
    const normalized = (status ?? '').trim().toLowerCase();
    if (normalized === 'expired' || normalized === 'expiring') {
      return normalized;
    }
    return 'due';
  }

  private getEffectiveApprovedDateString(app: SeniorCenterApplicationListItem): string | null {
    const date = this.getEffectiveApprovedDate(app);
    return date ? this.formatLocalDate(date) : null;
  }

  private getEffectiveApprovedDate(app: SeniorCenterApplicationListItem): Date | null {
    return this.parseApprovedDate(app.approvedDate) ?? this.parseApprovedDate(app.updatedAt);
  }

  private parseApprovedDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    const dateOnly = value.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      const [year, month, day] = dateOnly.split('-').map(Number);
      return new Date(year, month - 1, day);
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private buildRangeStamp(from: Date, to: Date): string {
    return `${this.formatLocalDate(from)}-to-${this.formatLocalDate(to)}`;
  }

  private formatLocalDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private matchesSelectedFinancialYear(
    submittedDate: string | null | undefined,
    year: number | null
  ): boolean {
    if (year == null) {
      return true;
    }

    if (!submittedDate) {
      return false;
    }

    const parsedYear = new Date(submittedDate).getFullYear();
    return Number.isFinite(parsedYear) && parsedYear === year;
  }

  private normalizeApplication(app: SeniorCenterApplicationListItem): SeniorCenterApplicationListItem {
    const record = app as SeniorCenterApplicationListItem & Record<string, unknown>;
    const rfidNumber =
      app.rfidNumber ??
      (record['RfidNumber'] != null ? String(record['RfidNumber']) : null);
    const approvedDate =
      app.approvedDate ??
      (record['ApprovedDate'] != null ? String(record['ApprovedDate']) : null);
    const updatedAt =
      app.updatedAt ??
      (record['UpdatedAt'] != null ? String(record['UpdatedAt']) : null);

    const transportationOption =
      app.transportationOption ??
      (record['TransportationOption'] != null ? String(record['TransportationOption']) : null);
    const transportationRequestStatus =
      app.transportationRequestStatus ??
      (record['TransportationRequestStatus'] != null
        ? String(record['TransportationRequestStatus'])
        : null);
    const transportationApprovedAt =
      app.transportationApprovedAt ??
      (record['TransportationApprovedAt'] != null
        ? String(record['TransportationApprovedAt'])
        : null);

    const expiresAt =
      app.expiresAt ??
      (record['ExpiresAt'] != null ? String(record['ExpiresAt']) : null);
    const daysUntilExpiryRaw =
      app.daysUntilExpiry ??
      (record['DaysUntilExpiry'] != null ? Number(record['DaysUntilExpiry']) : null);
    const daysUntilExpiry =
      daysUntilExpiryRaw != null && Number.isFinite(daysUntilExpiryRaw)
        ? Math.trunc(daysUntilExpiryRaw)
        : null;
    const expiryStatus =
      app.expiryStatus ??
      (record['ExpiryStatus'] != null ? String(record['ExpiryStatus']) : null);
    const previousApplicationId =
      app.previousApplicationId ??
      (record['PreviousApplicationId'] != null ? String(record['PreviousApplicationId']) : null);

    return {
      ...app,
      applicationId: this.getApplicationId(app),
      rfidNumber: rfidNumber?.trim() || null,
      approvedDate: approvedDate?.trim() || null,
      updatedAt: updatedAt?.trim() || null,
      transportationOption: transportationOption?.trim() || null,
      transportationRequestStatus: transportationRequestStatus?.trim() || null,
      transportationApprovedAt: transportationApprovedAt?.trim() || null,
      expiresAt: expiresAt?.trim() || null,
      daysUntilExpiry,
      expiryStatus: expiryStatus?.trim().toLowerCase() || null,
      previousApplicationId: previousApplicationId?.trim() || null,
    };
  }

  private buildColumns(tab: SeniorCenterListTab): NiTableColumn<SeniorCenterApplicationListItem>[] {
    const sharedColumns: NiTableColumn<SeniorCenterApplicationListItem>[] = [
      { key: 'applicantName', label: 'Applicant Name' },
      { key: 'applicantEmailAddress', label: 'Applicant Email Address' },
      {
        key: 'livesAlone',
        label: 'Lives Alone',
        formatter: (_, row) => this.formatLivesAlone(row.livesAlone),
      },
      {
        key: 'submittedDate',
        label: 'Date of Submission',
        formatter: (_, row) => this.formatSubmittedDate(row.submittedDate),
      },
      {
        key: 'approvedDate',
        label: 'Date of Approval',
        formatter: (_, row) => this.formatApprovedDate(row),
      },
    ];

    if (tab === 'transportation') {
      const transportColumns = sharedColumns.map((col) =>
        col.key === 'approvedDate'
          ? {
              ...col,
              label: 'Date of Transport Approval',
              formatter: (_: unknown, row: SeniorCenterApplicationListItem) =>
                this.formatTransportationApprovedDate(row),
            }
          : col,
      );
      return [
        ...transportColumns,
        {
          key: 'transportationOption',
          label: 'Transportation Request',
          formatter: (_, row) => this.formatTransportationRequest(row),
        },
        {
          key: 'actions',
          label: 'Action',
          custom: true,
        },
      ];
    }

    if (tab === 'expiring') {
      return [
        ...sharedColumns,
        {
          key: 'expiresAt',
          label: 'Expires',
          formatter: (_, row) => this.formatExpiresAt(row),
        },
        {
          key: 'expiryStatus',
          label: 'Renewal',
          custom: true,
        },
        {
          key: 'previousApplicationId',
          label: 'Prior App',
          formatter: (_, row) => this.formatPreviousApplicationId(row.previousApplicationId),
        },
        {
          key: 'actions',
          label: 'Action',
          custom: true,
        },
      ];
    }

    return [
      ...sharedColumns,
      {
        key: 'status',
        label: 'Status',
        formatter: (_, row) => this.formatStatus(row.status),
      },
      {
        key: 'rfidNumber',
        label: 'RFID Number',
        formatter: (_, row) => this.formatRfidNumber(row.rfidNumber),
      },
      {
        key: 'actions',
        label: 'Action',
        custom: true,
      },
    ];
  }

  formatExpiresAt(row: SeniorCenterApplicationListItem): string {
    const raw = row.expiresAt;
    if (!raw) return '-';
    const date = FormDateUtils.parseDateOnly(raw) ?? this.parseApprovedDate(raw);
    if (!date) return '-';
    return FormDateUtils.formatDate(date, 'MM/DD/YYYY');
  }

  formatExpiryStatusLabel(row: SeniorCenterApplicationListItem): string {
    const status = (row.expiryStatus ?? '').trim().toLowerCase();
    if (status === 'expired') {
      const days = row.daysUntilExpiry;
      if (days != null && days < 0) {
        return `Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`;
      }
      return 'Expired';
    }
    if (status === 'expiring') {
      const days = row.daysUntilExpiry;
      if (days == null) {
        return 'Expiring soon';
      }
      if (days === 0) {
        return 'Expires today';
      }
      return `Expires in ${days} day${days === 1 ? '' : 's'}`;
    }
    return status === 'current' ? 'Current' : '-';
  }

  expiryBadgeClass(row: SeniorCenterApplicationListItem): string {
    const status = (row.expiryStatus ?? '').trim().toLowerCase();
    if (status === 'expired') {
      return 'sc-expiry-badge sc-expiry-badge--expired';
    }
    if (status === 'expiring') {
      return 'sc-expiry-badge sc-expiry-badge--expiring';
    }
    return 'sc-expiry-badge';
  }

  formatPreviousApplicationId(value: string | null | undefined): string {
    const trimmed = (value ?? '').trim();
    return trimmed || '-';
  }

  formatSubmittedDate(value: string | null): string {
    if (!value) return '-';
    const date = FormDateUtils.parseDateOnly(value);
    if (!date) return '-';
    return FormDateUtils.formatDate(date, 'MM/DD/YYYY');
  }

  formatApprovedDate(row: SeniorCenterApplicationListItem): string {
    // Transportation tab: show transport decision date only — never the Senior Center app approval date.
    if (this.activeTab === 'transportation') {
      return this.formatTransportationApprovedDate(row);
    }

    const status = String(row.status ?? '').trim().toLowerCase();
    const isApproved = status === 'approved' || status === 'approve';
    // Show explicit approval date; fall back to updatedAt only for approved apps.
    const raw = row.approvedDate ?? (isApproved ? row.updatedAt : null);
    if (!raw) return '-';
    const date = FormDateUtils.parseDateOnly(raw) ?? this.parseApprovedDate(raw);
    if (!date) return '-';
    return FormDateUtils.formatDate(date, 'MM/DD/YYYY');
  }

  /**
   * Transport Approver "Date of Approval" — blank while pending; only after approve/reject.
   */
  private formatTransportationApprovedDate(row: SeniorCenterApplicationListItem): string {
    const requestStatus = (row.transportationRequestStatus ?? 'pending').trim().toLowerCase();
    if (requestStatus !== 'approved' && requestStatus !== 'rejected') {
      return '-';
    }

    // Prefer dedicated transport decision timestamp; fall back to row updatedAt after a decision.
    const raw = row.transportationApprovedAt?.trim() || row.updatedAt || null;
    if (!raw) return '-';
    const date = FormDateUtils.parseDateOnly(raw) ?? this.parseApprovedDate(raw);
    if (!date) return '-';
    return FormDateUtils.formatDate(date, 'MM/DD/YYYY');
  }

  formatLivesAlone(value: string | null | undefined): string {
    const v = (value ?? '').toString().trim().toLowerCase();
    if (!v) return '-';
    if (v === 'yes' || v === 'true') return 'Yes';
    if (v === 'no' || v === 'false') return 'No';
    return value || '-';
  }

  formatStatus(value: string | null | undefined): string {
    const normalized = normalizeApplicationStatus(value ?? '');
    if (!normalized) return '-';

    const labels: Record<string, string> = {
      draft: 'Draft',
      approved: 'Approved',
      approve: 'Approved',
      returned: 'Returned',
      rejected: 'Rejected',
      denied: 'Denied',
      pending: 'Pending',
      'pending approval': 'Pending',
      'pending aproval': 'Pending',
      'pending information': 'Pending Information',
      'in review': 'In Review',
      'not reviewed': 'Not Reviewed',
      submitted: 'Submitted',
    };

    const formatted = (value ?? '').trim().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return labels[normalized] ?? (formatted || '-');
  }

  formatRfidNumber(value: string | null | undefined): string {
    const trimmed = (value ?? '').trim();
    return trimmed || '-';
  }

  formatTransportationOption(value: string | null | undefined): string {
    const normalized = (value ?? '').trim().toLowerCase();
    if (!normalized) return '-';
    if (
      normalized === 'to center and from home' ||
      normalized === 'to centre and from home' ||
      normalized === 'both' ||
      normalized === 'round trip' ||
      normalized === 'roundtrip'
    ) {
      return 'Round Trip';
    }
    if (normalized === 'from home' || normalized === 'to home' || normalized === 'home') {
      return 'To Home';
    }
    if (
      normalized === 'to center' ||
      normalized === 'to centre' ||
      normalized === 'center' ||
      normalized === 'centre'
    ) {
      return 'To Center';
    }
    if (normalized === 'not required' || normalized === 'no need' || normalized === 'none') {
      return 'Not Required';
    }
    return value?.trim() || '-';
  }

  formatTransportationRequest(row: SeniorCenterApplicationListItem): string {
    const option = this.formatTransportationOption(row.transportationOption);
    const requestStatus = (row.transportationRequestStatus ?? 'pending').trim().toLowerCase();
    if (requestStatus === 'approved') {
      return `${option} (Approved)`;
    }
    if (requestStatus === 'rejected') {
      return `${option} (Rejected)`;
    }
    return `${option} (Pending review)`;
  }

  isApprovedApplication(app: SeniorCenterApplicationListItem): boolean {
    const status = normalizeApplicationStatus(app.status ?? '');
    return status === 'approved' || status === 'approve';
  }

  /** Submitted apps (pending/approved) can appear on Transportation when transport was requested. */
  private isEligibleForTransportationTab(app: SeniorCenterApplicationListItem): boolean {
    const status = normalizeApplicationStatus(app.status ?? '');
    if (!status || status === 'draft' || status === 'rejected' || status === 'returned') {
      return false;
    }
    return status === 'pending' || status === 'approved' || status === 'approve';
  }

  isTransportationPending(app: SeniorCenterApplicationListItem): boolean {
    const requestStatus = this.getTransportationRequestStatus(app);
    return requestStatus === 'pending';
  }

  isTransportationApproved(app: SeniorCenterApplicationListItem): boolean {
    return this.getTransportationRequestStatus(app) === 'approved';
  }

  isTransportationRejected(app: SeniorCenterApplicationListItem): boolean {
    return this.getTransportationRequestStatus(app) === 'rejected';
  }

  private getTransportationRequestStatus(app: SeniorCenterApplicationListItem): string {
    // No transport requested → never treat as pending for the Approver queue.
    if (!this.isTransportationRequested(app)) {
      return 'not_required';
    }
    const requestStatus = (app.transportationRequestStatus ?? '').trim().toLowerCase();
    if (!requestStatus || requestStatus === 'pending') {
      return 'pending';
    }
    if (requestStatus === 'not_required' || requestStatus === 'not required') {
      return 'not_required';
    }
    return requestStatus;
  }

  private matchesTransportationStatusFilter(app: SeniorCenterApplicationListItem): boolean {
    if (this.selectedStatus === 'all') {
      return true;
    }
    if (this.selectedStatus === 'pending') {
      return this.isTransportationPending(app);
    }
    if (this.selectedStatus === 'approved') {
      return this.isTransportationApproved(app);
    }
    if (this.selectedStatus === 'rejected') {
      return this.isTransportationRejected(app);
    }
    return true;
  }

  private resolveTransportationStatus(status: string | null): string {
    const normalized = (status ?? '').trim().toLowerCase();
    if (normalized === 'pending' || normalized === 'approved' || normalized === 'rejected') {
      return normalized;
    }
    return 'pending';
  }

  isTransportationRequested(app: SeniorCenterApplicationListItem): boolean {
    const normalized = (app.transportationOption ?? '').trim().toLowerCase();
    return (
      normalized === 'from home' ||
      normalized === 'to home' ||
      normalized === 'home' ||
      normalized === 'to center' ||
      normalized === 'to centre' ||
      normalized === 'center' ||
      normalized === 'centre' ||
      normalized === 'to center and from home' ||
      normalized === 'to centre and from home' ||
      normalized === 'both' ||
      normalized === 'round trip' ||
      normalized === 'roundtrip'
    );
  }

  canReviewTransportation(app: SeniorCenterApplicationListItem): boolean {
    // Only real transport requests go to the Approver (Not Required is skipped).
    return (
      this.isApprovedApplication(app) &&
      this.isTransportationPending(app) &&
      this.isTransportationRequested(app)
    );
  }

  /** Rejected transport can be restored by staff (Revoke → choose option → approve). */
  canRevokeTransportation(app: SeniorCenterApplicationListItem): boolean {
    return (
      this.isApprovedApplication(app) &&
      this.isTransportationRejected(app) &&
      this.isTransportationRequested(app)
    );
  }

  onApproveTransportation(row: SeniorCenterApplicationListItem): void {
    if (!this.canReviewTransportation(row) || this.isReviewing) {
      return;
    }

    this.openTransportApproveModal(row, false);
  }

  onRevokeTransportation(row: SeniorCenterApplicationListItem): void {
    if (!this.canRevokeTransportation(row) || this.isReviewing) {
      return;
    }

    this.openTransportApproveModal(row, true);
  }

  private openTransportApproveModal(
    row: SeniorCenterApplicationListItem,
    isRevoke: boolean
  ): void {
    this.reviewingApplication = row;
    this.isRevokingTransportation = isRevoke;
    this.reviewTransportationOption = this.formatTransportationOption(row.transportationOption);
    if (this.reviewTransportationOption === '-') {
      this.reviewTransportationOption = 'To Center';
    }
    // Revoke modal only allows real trip options.
    if (
      isRevoke &&
      !this.revokeTransportationOptionChoices.some(
        (opt) => opt.value === this.reviewTransportationOption
      )
    ) {
      this.reviewTransportationOption = 'To Center';
    }
    this.reviewFundingTypes = [];
    this.reviewFundingOverride = false;
    this.reviewDistanceMiles = null;
    this.distanceLoadError = null;
    this.reviewError = null;
    this.showRejectModal = false;
    this.showTransportApproveModal = true;
    void this.loadReviewDistanceAndPrefillFunding(row);
  }

  isFundingTypeSelected(value: string): boolean {
    return this.reviewFundingTypes.includes(value);
  }

  isFundingTypeEligible(value: string): boolean {
    if (this.reviewFundingOverride) {
      return true;
    }
    // Until distance loads, keep options enabled so staff can still work.
    if (this.reviewDistanceMiles == null) {
      return true;
    }
    if (value === FUNDING_AAA) {
      return this.reviewDistanceMiles <= AAA_TRANSPORTATION_MAX_MILES;
    }
    if (value === FUNDING_METRO) {
      return this.reviewDistanceMiles <= METRO_TRANSPORTATION_MAX_MILES;
    }
    return true;
  }

  onToggleFundingType(value: string, checked: boolean): void {
    if (checked && !this.isFundingTypeEligible(value)) {
      return;
    }
    if (checked) {
      if (!this.reviewFundingTypes.includes(value)) {
        this.reviewFundingTypes = [...this.reviewFundingTypes, value];
      }
      return;
    }
    this.reviewFundingTypes = this.reviewFundingTypes.filter((v) => v !== value);
  }

  onToggleFundingOverride(checked: boolean): void {
    this.reviewFundingOverride = checked;
    if (!checked) {
      // Drop ineligible selections when override is turned off.
      this.reviewFundingTypes = this.reviewFundingTypes.filter((v) => this.isFundingTypeEligible(v));
    }
  }

  private async loadReviewDistanceAndPrefillFunding(
    row: SeniorCenterApplicationListItem
  ): Promise<void> {
    const applicationId = this.getApplicationId(row);
    if (!applicationId) {
      return;
    }

    this.isLoadingDistance = true;
    this.distanceLoadError = null;
    try {
      const formResponse = await firstValueFrom(
        this.seniorCenterService.getApplicationForm(applicationId).pipe(catchError(() => of(null)))
      );
      const formJson = (formResponse?.formJson ?? {}) as Record<string, unknown>;
      this.prefillFundingFromFormJson(formJson);

      const applicantAddress = this.extractResidentialAddressFromFormJson(formJson);
      const centerAddress = String(getSelectedLocation()?.addressLine ?? '').trim();
      if (!applicantAddress || !centerAddress) {
        this.distanceLoadError = !applicantAddress
          ? 'Applicant address missing — distance could not be calculated.'
          : 'Center address missing — distance could not be calculated.';
        this.reviewDistanceMiles = null;
        return;
      }

      const [miles] = await this.googleMapsLoader.getDrivingDistancesMiles(applicantAddress, [
        centerAddress,
      ]);
      this.reviewDistanceMiles =
        miles != null && Number.isFinite(miles) ? Math.round(miles * 10) / 10 : null;
      if (this.reviewDistanceMiles == null) {
        this.distanceLoadError = 'Unable to calculate driving distance.';
      } else {
        // Drop prefilled funding that is ineligible for the measured distance.
        this.reviewFundingTypes = this.reviewFundingTypes.filter((v) =>
          this.isFundingTypeEligible(v)
        );
        this.applyDefaultFundingFromDistance();
      }
    } catch {
      this.distanceLoadError = 'Unable to calculate driving distance.';
      this.reviewDistanceMiles = null;
    } finally {
      this.isLoadingDistance = false;
    }
  }

  private prefillFundingFromFormJson(formJson: Record<string, unknown>): void {
    const raw =
      formJson['transportation_funding_types'] ??
      formJson['transportationFundingTypes'] ??
      (formJson['client_preferences'] as Record<string, unknown> | undefined)?.[
        'transportation_funding_types'
      ];
    const values = Array.isArray(raw)
      ? raw.map((v) => String(v ?? '').trim()).filter(Boolean)
      : typeof raw === 'string' && raw.trim()
        ? raw.split(',').map((v) => v.trim()).filter(Boolean)
        : [];
    const normalized = values
      .map((v) => {
        const lower = v.toLowerCase();
        if (lower.includes('aaa')) {
          return FUNDING_AAA;
        }
        if (lower.includes('metro')) {
          return FUNDING_METRO;
        }
        return v;
      })
      .filter((v, i, arr) => arr.indexOf(v) === i);
    if (normalized.length > 0) {
      this.reviewFundingTypes = normalized;
    }
  }

  /** Suggest funding when none prefilled yet. */
  private applyDefaultFundingFromDistance(): void {
    if (this.reviewFundingTypes.length > 0 || this.reviewDistanceMiles == null) {
      return;
    }
    const suggested: string[] = [];
    if (this.reviewDistanceMiles <= AAA_TRANSPORTATION_MAX_MILES) {
      suggested.push(FUNDING_AAA, FUNDING_METRO);
    } else if (this.reviewDistanceMiles <= METRO_TRANSPORTATION_MAX_MILES) {
      suggested.push(FUNDING_METRO);
    }
    this.reviewFundingTypes = suggested;
  }

  private extractResidentialAddressFromFormJson(formJson: Record<string, unknown>): string {
    const addresses = Array.isArray(formJson['address'])
      ? (formJson['address'] as Record<string, unknown>[])
      : Array.isArray(formJson['addresses'])
        ? (formJson['addresses'] as Record<string, unknown>[])
        : [];
    const residential =
      addresses.find((a) => {
        const type = String(a?.['address_type'] ?? a?.['addressType'] ?? '').toLowerCase();
        return type === 'residential' || type === '';
      }) ?? addresses[0];

    if (residential) {
      const line = String(
        residential['address_line'] ?? residential['addressLine'] ?? ''
      ).trim();
      const city = String(residential['city'] ?? '').trim();
      const state = String(residential['state'] ?? '').trim();
      const zip = String(
        residential['postal_code'] ?? residential['postalCode'] ?? ''
      ).trim();
      const composed = [line, city, state, zip].filter(Boolean).join(', ');
      if (composed) {
        return composed;
      }
    }

    // Fallback: discrete residential field ids used on Senior Center form.
    const line = String(formJson['1007'] ?? '').trim();
    const city = String(formJson['1009'] ?? '').trim();
    const state = String(formJson['1010'] ?? '').trim();
    const zip = String(formJson['1008'] ?? '').trim();
    return [line, city, state, zip].filter(Boolean).join(', ');
  }

  onRejectTransportation(row: SeniorCenterApplicationListItem): void {
    if (!this.canReviewTransportation(row) || this.isReviewing) {
      return;
    }

    this.reviewingApplication = row;
    this.isRevokingTransportation = false;
    this.rejectComment = '';
    this.reviewError = null;
    this.showTransportApproveModal = false;
    this.showRejectModal = true;
  }

  onCloseTransportApproveModal(): void {
    if (this.isReviewing) {
      return;
    }

    this.showTransportApproveModal = false;
    this.reviewError = null;
    this.reviewingApplication = null;
    this.reviewTransportationOption = '';
    this.reviewFundingTypes = [];
    this.reviewFundingOverride = false;
    this.reviewDistanceMiles = null;
    this.distanceLoadError = null;
    this.isLoadingDistance = false;
    this.isRevokingTransportation = false;
  }

  onReviewTransportationOptionChange(event: Event): void {
    const select = event.target as HTMLSelectElement | null;
    this.reviewTransportationOption = select?.value ?? '';
    if (!this.requiresFundingTypes) {
      this.reviewFundingTypes = [];
    } else if (this.reviewFundingTypes.length === 0) {
      this.applyDefaultFundingFromDistance();
    }
  }

  onCloseRejectModal(): void {
    if (this.isReviewing) {
      return;
    }

    this.showRejectModal = false;
    this.rejectComment = '';
    this.reviewError = null;
    this.reviewingApplication = null;
  }

  onRejectCommentChange(comment: string): void {
    this.rejectComment = comment;
    this.reviewError = null;
  }

  confirmApproveTransportation(): void {
    const applicationId = this.getApplicationId(this.reviewingApplication);
    if (!applicationId || this.isReviewing) {
      return;
    }

    const option = (this.reviewTransportationOption || '').trim();
    if (!option) {
      this.reviewError = this.isRevokingTransportation
        ? 'Please select a transportation option to approve.'
        : 'Please select a transportation before approving.';
      return;
    }

    if (
      this.isRevokingTransportation &&
      !this.revokeTransportationOptionChoices.some((opt) => opt.value === option)
    ) {
      this.reviewError = 'Please select To Center, To Home, or Round Trip.';
      return;
    }

    const fundingTypes = this.requiresFundingTypes
      ? this.reviewFundingTypes.filter((v) => this.isFundingTypeEligible(v) || this.reviewFundingOverride)
      : [];
    if (this.requiresFundingTypes && fundingTypes.length === 0) {
      this.reviewError =
        this.isBeyondMetroMiles && !this.reviewFundingOverride
          ? 'Distance exceeds 7.5 miles. Enable override to select a funding type, or set Transportation to Not Required.'
          : 'Please select at least one funding type (AAA and/or Metro).';
      return;
    }

    this.isReviewing = true;
    this.reviewError = null;
    this.seniorCenterService
      .reviewTransportation(applicationId, {
        status: 'approved',
        transportationOption: option,
        transportationFundingTypes: fundingTypes.length > 0 ? fundingTypes : null,
        distanceToCenterMiles: this.reviewDistanceMiles,
      })
      .subscribe({
        next: () => {
          this.isReviewing = false;
          this.showTransportApproveModal = false;
          this.reviewingApplication = null;
          this.reviewTransportationOption = '';
          this.reviewFundingTypes = [];
          this.reviewFundingOverride = false;
          this.reviewDistanceMiles = null;
          this.distanceLoadError = null;
          this.isRevokingTransportation = false;
          this.reloadTrigger$.next();
        },
        error: (err) => {
          this.reviewError =
            err?.error?.message ??
            (this.isRevokingTransportation
              ? 'Unable to revoke rejection and approve transportation.'
              : 'Unable to approve transportation request.');
          this.isReviewing = false;
        },
      });
  }

  confirmRejectTransportation(): void {
    const applicationId = this.getApplicationId(this.reviewingApplication);
    if (!applicationId || this.isReviewing) {
      return;
    }

    const comment = this.rejectComment.trim();
    if (!comment) {
      this.reviewError = 'Rejection comment is required.';
      return;
    }

    this.isReviewing = true;
    this.reviewError = null;
    this.seniorCenterService
      .reviewTransportation(applicationId, {
        status: 'rejected',
        comment,
      })
      .subscribe({
        next: () => {
          this.isReviewing = false;
          this.showRejectModal = false;
          this.rejectComment = '';
          this.reviewingApplication = null;
          this.reloadTrigger$.next();
        },
        error: (err) => {
          this.reviewError = err?.error?.message ?? 'Unable to reject transportation request.';
          this.isReviewing = false;
        },
      });
  }

  isDraftApplication(app: SeniorCenterApplicationListItem): boolean {
    return normalizeApplicationStatus(app.status ?? '') === 'draft';
  }

  /** Draft, rejected, or returned — staff can continue without re-entering data. */
  canContinueApplication(app: SeniorCenterApplicationListItem): boolean {
    const status = normalizeApplicationStatus(app.status ?? '');
    return status === 'draft' || status === 'rejected' || status === 'returned' || status === 'denied';
  }

  onContinueDraftApplication(applicationId: string, status?: string | null): void {
    const id = applicationId?.trim();
    if (!id) {
      return;
    }

    const normalized = normalizeApplicationStatus(status ?? '');
    const needsReopen =
      normalized === 'rejected' || normalized === 'returned' || normalized === 'denied';

    const navigateToEnrollment = (): void => {
      sessionStorage.setItem('programName', 'Senior Center');
      sessionStorage.setItem('staff_enrollment_mode', 'true');
      sessionStorage.setItem('program_status', 'draft');
      void this.router.navigate(['/program', '1000'], {
        queryParams: {
          staffEnrollment: 'true',
          applicationId: id,
          resumeDraft: 'true',
        },
      });
    };

    if (!needsReopen) {
      navigateToEnrollment();
      return;
    }

    // Soft-reopen as draft so form_json is kept and staff can edit/resubmit.
    this.seniorCenterService.reopenApplicationAsDraft(id).subscribe({
      next: () => navigateToEnrollment(),
      error: (err) => {
        this.errorMessage =
          err?.error?.message ??
          'Unable to reopen application for editing. The saved data may still be available — try again.';
      },
    });
  }

  onViewApplication(applicationId: string): void {
    const id = applicationId?.trim();
    if (!id) {
      return;
    }

    sessionStorage.setItem('programName', 'Senior Center');
    void this.router.navigate(['/program', '1000'], {
      queryParams: {
        view: 'true',
        applicationId: id,
      },
    });
  }

  onViewTransportationApplication(applicationId: string): void {
    const id = applicationId?.trim();
    if (!id) {
      return;
    }

    sessionStorage.setItem('programName', 'Senior Center');
    void this.router.navigate(['/program', '1000'], {
      queryParams: {
        view: 'true',
        applicationId: id,
        from: 'transportation',
      },
    });
  }

  private getApplicationId(row: SeniorCenterApplicationListItem | null | undefined): string {
    if (!row) {
      return '';
    }

    const record = row as SeniorCenterApplicationListItem & Record<string, unknown>;
    const id =
      record.applicationId ??
      record['ApplicationId'] ??
      record['id'] ??
      record['Id'];
    return id != null ? String(id).trim() : '';
  }
}
